"use client";

import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Star, Quote, Hand } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const testimonials = [{
  name: "Sarah Miller",
  position: "West Palm Beach, FL",
  quote: "Joss was my rock during labor. Her calm energy and deep knowledge transformed a scary situation into a beautiful, empowered birth experience.",
  metric: "Empowered Birth",
  topVoice: true,
  avatar: "https://sunny-rooster.10web.cloud/wp-content/uploads/2026/05/IMG_1710_edited_edited.avif?v=u08y8304ve"
}, {
  name: "Elena Rodriguez",
  position: "Jupiter, FL",
  quote: "I was struggling so much with breastfeeding until Joss arrived. Her gentle guidance and lactation support saved my nursing journey.",
  metric: "Lactation Success",
  topVoice: false,
  avatar: "https://sunny-rooster.10web.cloud/wp-content/uploads/2026/05/IMG_4503-scaled.jpeg?v=61fr7gs7pc2"
}, {
  name: "Maya Thompson",
  position: "Palm Beach Gardens, FL",
  quote: "The postpartum care was a absolute godsend. I felt seen, heard, and deeply cared for during those fragile first weeks at home.",
  metric: "Restful Recovery",
  topVoice: true,
  avatar: "https://sunny-rooster.10web.cloud/wp-content/uploads/2026/05/Resized_20240916_023730_122389330399299.jpg?v=iwxg5e2rfn"
}, {
  name: "Jessica Lewis",
  position: "Wellington, FL",
  quote: "Her support during my VBAC was incredible. She helped me advocate for myself and achieve the healing birth I had dreamed of.",
  metric: "Healing VBAC",
  topVoice: false,
  avatar: "https://sunny-rooster.10web.cloud/wp-content/uploads/2026/05/d75474_e58ebb66ef804b5ca12da881bbf4f6dfmv2.avif?v=6vvn7kdfnzc"
}];
export default function Testimonials() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="222774" id="voices-of-joy" className="py-24 md:py-32 relative overflow-hidden text-secondary bg-secondary">
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-chart-1/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-chart-2/5 rounded-full blur-[150px] pointer-events-none" />
      
      <div className="absolute inset-0 opacity-20 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none" />

      <div ref={ref} className={`container mx-auto px-4 relative z-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="text-center mb-20">
          <h2 className="text-4xl font-serif mb-6 tracking-tight !font-normal md:text-5xl lg:text-6xl text-primary">
            Voices of <span className="italic">Joy</span>
          </h2>
          <div className="flex items-center justify-center gap-3 text-primary/60 text-xs md:text-sm font-default uppercase tracking-widest">
            <Hand className="w-4 h-4 animate-bounce" />
            <span>Swipe to share their stories</span>
          </div>
        </div>

        <Carousel opts={{
        align: "center",
        loop: true
      }} className="w-full cursor-grab active:cursor-grabbing">
          <CarouselContent className="-ml-4 md:-ml-6 pb-12 pt-16">
            {testimonials.map((testimonial, i) => <CarouselItem key={i} data-index={i} className="pl-4 md:pl-6 md:basis-1/2 lg:basis-1/3">
                <Card className="gap-6 py-6 h-full relative border-none shadow-none flex flex-col justify-between group transition-all duration-500 rounded-lg overflow-visible pt-14 text-center text-primary bg-background">
                  
                  <div className="absolute -top-12 left-1/2 -translate-x-1/2 z-20">
                    <div className={`relative rounded-full p-0.5 transition-transform duration-700 group-hover:scale-105 ${testimonial.topVoice ? "bg-gradient-to-tr from-chart-1/40 via-chart-1/10 to-chart-1/40 shadow-sm" : "bg-border/20"}`}>
                      <SafeHydrate><Avatar className="w-24 h-24 border-2 border-background bg-background shadow-md">
                        <AvatarImage src={testimonial.avatar} alt={testimonial.name} className="object-cover" />
                        <AvatarFallback className="text-2xl font-serif font-normal bg-muted text-primary">
                          {testimonial.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar></SafeHydrate>
                      
                      {testimonial.topVoice && <div className="absolute -bottom-1 -right-1 bg-background rounded-full p-1.5 shadow-sm border border-border/50">
                          <Star className="w-4 h-4 text-chart-1 fill-chart-1" />
                        </div>}
                    </div>
                  </div>

                  <CardContent className="flex-grow flex flex-col pt-6 px-4 !text-[rgba(0,0,0,1)] md:px-8">
                    <Quote className="w-8 h-8 text-chart-1/20 mb-6 mx-auto" />
                    <p className="text-xl md:text-2xl font-serif italic leading-relaxed mb-8 flex-grow text-primary">
                      "{testimonial.quote}"
                    </p>
                    <div className="mt-auto">
                      <h4 className="font-default uppercase tracking-widest text-xs font-semibold text-primary">
                        {testimonial.name}
                      </h4>
                      <p className="text-[10px] font-default uppercase tracking-widest mt-1 text-accent">
                        {testimonial.position}
                      </p>
                    </div>
                  </CardContent>

                  <CardFooter className="px-8 pb-8 pt-6 mt-6">
                    <div className="w-full">
                      <p className="text-[9px] uppercase tracking-[0.2em] mb-2 font-bold text-primary">
                        Outcome
                      </p>
                      <div className="text-2xl md:text-3xl font-serif italic drop-shadow-sm text-accent">
                        {testimonial.metric}
                      </div>
                    </div>
                  </CardFooter>
                </Card>
              </CarouselItem>)}
          </CarouselContent>
          
          <div className="flex items-center justify-center gap-6 mt-12">
            <CarouselPrevious className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border dark:bg-input/30 dark:border-input dark:hover:bg-input/50 size-8 top-1/2 -left-12 static translate-y-0 translate-x-0 w-10 h-10 rounded-full border-border/30 text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all shadow-sm bg-background" />
            <CarouselNext className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border dark:bg-input/30 dark:border-input dark:hover:bg-input/50 size-8 top-1/2 -right-12 static translate-y-0 translate-x-0 w-10 h-10 rounded-full border-border/30 text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all shadow-sm bg-background" />
          </div>
        </Carousel>
      </div>
    </section>;
}